import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { email as isEmail } from 'lib/form-validation'
import { setGiftGiverCookie, useGiftGiverCookie } from 'lib/cookies'
import { find, includes, isString } from 'lodash'
import { DangerAlert } from 'components/alerts'
import { InfoButton } from 'components/buttons'
import { useMutation } from 'react-query'
import {
  Checkbox,
  Input,
  Radio,
  RadioGroup,
  RelationshipToBabySelector,
} from 'components/forms'
import { getGiftCardsByType } from 'cart/lib'
import MailcheckedInput from 'components/mailchecked-input'
import GiftAmountSelect from './gift-amount-select'
import QuantityField from './quantity-field'

import {
  DEFAULT_GIFT_CARD_AMOUNT,
  GIFT_CARD_LABEL_DIGITAL,
  GIFT_CARD_LABEL_PHYSICAL,
  MARKETING_EMAIL_CHECKBOX_LABEL,
  PRODUCT_TYPE_DIGITAL,
  PRODUCT_TYPE_PHYSICAL_GIFT_CARD,
  RESERVE_ACTION_CHECKOUT,
  RESERVE_ACTION_PURCHASED,
  RESERVE_ACTION_SHOP,
} from '../constants'

const AddToCartForm = ({ className, onFormSubmit, regItem }) => {
  const {
    isFavor,
    isGiftCard,
    quantityNeeded,
    giftCardOptions,
    purchaseQuantityLimit,
    showPurchaseQuantityLimit,
    registryId,
  } = regItem
  const [reserveAction, setReserveAction] = useState()

  const { name, email, subscribeToMarketingEmail, relationshipToBaby } =
    useGiftGiverCookie(registryId)

  const defaultValues = {
    addToCart: {
      email,
      name,
      subscribeToMarketingEmail,
      giftAmount: isGiftCard ? DEFAULT_GIFT_CARD_AMOUNT : null,
      giftCardType: PRODUCT_TYPE_DIGITAL,
      quantity: 1,
      relationshipToBaby,
    },
  }

  const { errors, handleSubmit, register, setValue, watch } = useForm({
    defaultValues,
  })

  const [mutate, { error, isLoading, isError }] = useMutation(
    ({ submitBtnClicked, json }) => onFormSubmit({ ...json, submitBtnClicked })
  )

  const updateEmail = (eventOrValue) => {
    const value = isString(eventOrValue)
      ? eventOrValue
      : eventOrValue.target.value
    setValue('email', value)
  }

  const watchGiftCardType = watch(
    'giftCardType',
    defaultValues.addToCart.giftCardType
  )

  const giftCardsByType = isGiftCard
    ? getGiftCardsByType(giftCardOptions)
    : null

  const giftCardTypes = isGiftCard
    ? giftCardsByType && Object.keys(giftCardsByType)
    : null

  const handleSubmitReservation = (values) => {
    let json

    setGiftGiverCookie({
      relationshipToBaby: values.relationshipToBaby,
      registryId,
    })

    if (isGiftCard) {
      const giftCardProduct = find(
        giftCardsByType[values.giftCardType],
        (gc) => gc.price === parseFloat(values.giftAmount)
      )
      const productId = giftCardProduct ? giftCardProduct.id : null

      json = {
        email: values.email,
        giftAmount: values.giftAmount,
        name: values.name,
        subscribeToMarketingEmail: values.subscribeToMarketingEmail,
        productId,
        relationshipToBaby: values.relationshipToBaby,
      }
    } else {
      json = values
    }
    mutate({ submitBtnClicked: reserveAction, json })
  }

  return (
    <form
      className={className}
      onSubmit={handleSubmit(handleSubmitReservation)}
    >
      {isError && <DangerAlert>{error._error}</DangerAlert>}
      <fieldset className="mvn">
        <p className="h6 lh-base mbm mtn">
          {isGiftCard
            ? `Gift cards can be printed out and given in person or sent via email${
                includes(giftCardTypes, PRODUCT_TYPE_PHYSICAL_GIFT_CARD)
                  ? ' or mail'
                  : ''
              }.`
            : "Other gift givers won't see this item as available. You can change your mind later!"}
        </p>
        <fieldset>
          <Input
            name="name"
            defaultValue={defaultValues.addToCart.name}
            error={errors.name && errors.name.message}
            label="Your Name"
            placeholder="First and last name"
            type="text"
            inputRef={register({
              required: 'Please add your name',
            })}
          />
          <MailcheckedInput
            defaultValue={defaultValues.addToCart.email}
            error={errors?.email?.message}
            label="Your Email"
            placeholder="Your email address"
            name="email"
            inputRef={register({
              required: 'Enter a valid email address',
              validate: isEmail,
            })}
            onChange={updateEmail}
            mailcheckOnBlur={false}
            mailcheckOnChange
            mailcheckDelay={300}
          />
          <RelationshipToBabySelector
            inputRef={register}
            error={errors?.relationshipToBaby?.message}
            selectedOption={defaultValues.addToCart.relationshipToBaby}
          />
          <Checkbox
            name="subscribeToMarketingEmail"
            defaultChecked={defaultValues.addToCart.subscribeToMarketingEmail}
            label={MARKETING_EMAIL_CHECKBOX_LABEL}
            type="checkbox"
            value="true"
            inputRef={register()}
          />
        </fieldset>
      </fieldset>
      {quantityNeeded > 1 ? (
        <fieldset className="mbl mtn">
          <QuantityField
            name="quantity"
            defaultValue={defaultValues.addToCart.quantity}
            quantityNeeded={quantityNeeded}
            purchaseQuantityLimit={purchaseQuantityLimit}
            showPurchaseQuantityLimit={showPurchaseQuantityLimit}
            inputRef={register({
              required: true,
            })}
          />
        </fieldset>
      ) : null}
      {isGiftCard && giftCardsByType[watchGiftCardType] && (
        <>
          <GiftAmountSelect
            name="giftAmount"
            amount={defaultValues.addToCart.giftAmount}
            defaultGiftAmount={DEFAULT_GIFT_CARD_AMOUNT}
            defaultValue={defaultValues.addToCart.giftAmount}
            options={giftCardsByType[watchGiftCardType].map((gc) => gc.price)}
            inputRef={register({
              required: true,
            })}
          />
          <RadioGroup
            title="Card Type"
            className={giftCardTypes.length === 1 ? 'hidden' : null}
          >
            {giftCardTypes.map((cardType) => (
              <Radio
                defaultChecked={cardType === PRODUCT_TYPE_DIGITAL}
                name="giftCardType"
                inputRef={register({
                  required: true,
                })}
                label={
                  cardType === PRODUCT_TYPE_DIGITAL
                    ? GIFT_CARD_LABEL_DIGITAL
                    : GIFT_CARD_LABEL_PHYSICAL
                }
                tappable
                value={cardType}
              />
            ))}
          </RadioGroup>
        </>
      )}
      {isFavor ? (
        <InfoButton
          block
          submitting={isLoading}
          type="submit"
          className="mbm"
          onClick={(e) => setReserveAction(RESERVE_ACTION_SHOP)}
        >
          Reserve
        </InfoButton>
      ) : (
        <div>
          <InfoButton
            block
            submitting={isLoading && reserveAction == RESERVE_ACTION_SHOP}
            disabled={isLoading && reserveAction != RESERVE_ACTION_SHOP}
            type="submit"
            className="mbm"
            onClick={(e) => setReserveAction(RESERVE_ACTION_SHOP)}
          >
            Reserve & Keep Shopping
          </InfoButton>
          <InfoButton
            block
            empty
            submitting={isLoading && reserveAction == RESERVE_ACTION_CHECKOUT}
            disabled={isLoading && reserveAction != RESERVE_ACTION_CHECKOUT}
            type="submit"
            className="mbm"
            onClick={(e) => setReserveAction(RESERVE_ACTION_CHECKOUT)}
          >
            Reserve & Checkout
          </InfoButton>
          {!isGiftCard ? (
            <InfoButton
              block
              empty
              submitting={
                isLoading && reserveAction == RESERVE_ACTION_PURCHASED
              }
              disabled={isLoading && reserveAction != RESERVE_ACTION_PURCHASED}
              type="submit"
              onClick={(e) => setReserveAction(RESERVE_ACTION_PURCHASED)}
            >
              I've Purchased This
            </InfoButton>
          ) : null}
        </div>
      )}
    </form>
  )
}

AddToCartForm.propTypes = {
  className: PropTypes.string,
  onFormSubmit: PropTypes.func.isRequired,
  regItem: PropTypes.object.isRequired,
}

AddToCartForm.defaultProps = {
  className: undefined,
}

export default AddToCartForm
